import React from "react";
import { httpsCallable } from "firebase/functions";
import { functions, db } from "@/lib/firebase";
import { ToastContext } from "@/components/ToastProvider.jsx";
import { formatDT } from "@/utils/dates";
import { collection, onSnapshot, query, orderBy, limit, where, getDocs } from "firebase/firestore";
import { 
  Wallet, 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Users,
  Clock,
  Package,
  CreditCard,
  AlertCircle,
  CheckCircle,
  Info,
  Eye,
  UserCheck,
  Search,
  Filter
} from "lucide-react";

function Badge({children, variant = "default"}){ 
  const variants = {
    default: "bg-gray-100 text-gray-800",
    success: "bg-green-100 text-green-800",
    warning: "bg-yellow-100 text-yellow-800",
    error: "bg-red-100 text-red-800",
    info: "bg-blue-100 text-blue-800"
  };
  return <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${variants[variant]}`}>{children}</span>; 
}

export default function RiderWallets() {
  const { notify } = React.useContext(ToastContext);
  
  const [riders, setRiders] = React.useState([]);
  const [cashoutRequests, setCashoutRequests] = React.useState([]);
  const [selectedRider, setSelectedRider] = React.useState(null);
  const [riderTransactions, setRiderTransactions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [searchTerm, setSearchTerm] = React.useState("");
  const [filterStatus, setFilterStatus] = React.useState("all");
  const [walletModal, setWalletModal] = React.useState({ open: false, rider: null });
  const [cashoutModal, setCashoutModal] = React.useState({ open: false, request: null });

  // Load initial data
  React.useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        
        // Get all rider wallets
        const walletsRef = collection(db, "riderWallets");
        const walletsSnap = await getDocs(walletsRef);
        const riderWallets = walletsSnap.docs.map(doc => ({ 
          riderId: doc.id, 
          ...doc.data() 
        }));
        
        // Enhance rider wallets with user profile data
        const enhancedRiderWallets = await Promise.all(
          riderWallets.map(async (wallet) => {
            try {
              // Fetch rider profile from users collection
              const userDoc = await getDocs(query(
                collection(db, "users"), 
                where("uid", "==", wallet.riderId)
              ));
              
              if (!userDoc.empty) {
                const userData = userDoc.docs[0].data();
                return {
                  ...wallet,
                  riderName: userData.firstName && userData.lastName 
                    ? `${userData.firstName} ${userData.lastName}`.trim()
                    : userData.displayName || userData.email || 'Unknown Rider',
                  riderEmail: userData.email,
                  riderPhone: userData.phone
                };
              }
              
              return wallet;
            } catch (error) {
              console.warn('Error fetching rider profile for wallet:', wallet.riderId, error);
              return wallet;
            }
          })
        );
        
        setRiders(enhancedRiderWallets);
        setError("");
      } catch (e) {
        setError(e?.message || "Failed to load rider wallets");
        notify(e?.message || "Failed to load rider wallets", { type: "error" });
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [notify]);

  // Real-time updates for rider wallets
  React.useEffect(() => {
    const walletsRef = collection(db, "riderWallets");
    const q = query(walletsRef, orderBy("lastUpdated", "desc"));
    
    const unsub = onSnapshot(q, async (snap) => {
      const riderWallets = snap.docs.map(doc => ({ 
        riderId: doc.id, 
        ...doc.data() 
      }));
      
      // Enhance rider wallets with user profile data
      const enhancedRiderWallets = await Promise.all(
        riderWallets.map(async (wallet) => {
          try {
            // Fetch rider profile from users collection
            const userDoc = await getDocs(query(
              collection(db, "users"), 
              where("uid", "==", wallet.riderId)
            ));
            
            if (!userDoc.empty) {
              const userData = userDoc.docs[0].data();
              return {
                ...wallet,
                riderName: userData.firstName && userData.lastName 
                  ? `${userData.firstName} ${userData.lastName}`.trim()
                  : userData.displayName || userData.email || 'Unknown Rider',
                riderEmail: userData.email,
                riderPhone: userData.phone
              };
            }
            
            return wallet;
          } catch (error) {
            console.warn('Error fetching rider profile for wallet:', wallet.riderId, error);
            return wallet;
          }
        })
      );
      
      setRiders(enhancedRiderWallets);
    }, (e) => {
      console.error('Rider wallets listener error:', e);
    });

    return () => unsub();
  }, []);

  // Real-time updates for cashout requests
  React.useEffect(() => {
    const cashoutRef = collection(db, "riderCashouts");
    const q = query(cashoutRef, orderBy("createdAt", "desc"), limit(100));
    
    const unsub = onSnapshot(q, async (snap) => {
      const requests = snap.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      
      // Enhance cashout requests with rider profile data
      const enhancedRequests = await Promise.all(
        requests.map(async (request) => {
          try {
            if (request.riderId) {
              // Fetch rider profile from users collection
              const userDoc = await getDocs(query(
                collection(db, "users"), 
                where("uid", "==", request.riderId)
              ));
              
              if (!userDoc.empty) {
                const userData = userDoc.docs[0].data();
                return {
                  ...request,
                  riderName: userData.firstName && userData.lastName 
                    ? `${userData.firstName} ${userData.lastName}`.trim()
                    : userData.displayName || userData.email || 'Unknown Rider',
                  riderEmail: userData.email,
                  riderPhone: userData.phone
                };
              }
            }
            
            return request;
          } catch (error) {
            console.warn('Error fetching rider profile for cashout request:', request.id, error);
            return request;
          }
        })
      );
      
      setCashoutRequests(enhancedRequests);
    }, (e) => {
      console.error('Cashout requests listener error:', e);
    });

    return () => unsub();
  }, []);

  // Load rider transactions when rider is selected
  React.useEffect(() => {
    if (!selectedRider) {
      setRiderTransactions([]);
      return;
    }

    const transactionsRef = collection(db, "riderTransactions");
    const q = query(
      transactionsRef, 
      where("riderId", "==", selectedRider.riderId),
      orderBy("createdAt", "desc"),
      limit(50)
    );
    
    const unsub = onSnapshot(q, (snap) => {
      const txs = snap.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      setRiderTransactions(txs);
    }, (e) => {
              // Rider transactions listener error
    });

    return () => unsub();
  }, [selectedRider]);

  async function processCashout(requestId, action, notes = "") {
    try {
      const fn = httpsCallable(functions, "processRiderCashout");
      await fn({
        requestId,
        action, // "approve" or "reject"
        adminNotes: notes
      });

      notify(`Cashout request ${action}d successfully!`, { type: "success" });
      setCashoutModal({ open: false, request: null });
    } catch (e) {
      notify(e?.message || `Failed to ${action} cashout request`, { type: "error" });
    }
  }

  function openRiderWallet(rider) {
    setSelectedRider(rider);
    setWalletModal({ open: true, rider });
  }

  function closeWalletModal() {
    setWalletModal({ open: false, rider: null });
    setSelectedRider(null);
  }

  function openCashoutModal(request) {
    setCashoutModal({ open: true, request });
  }

  function closeCashoutModal() {
    setCashoutModal({ open: false, request: null });
  }

  function getStatusBadge(status) {
    switch (status) {
      case "pending":
        return <Badge variant="warning">Pending</Badge>;
      case "approved":
        return <Badge variant="success">Approved</Badge>;
      case "rejected":
        return <Badge variant="error">Rejected</Badge>;
      case "completed":
        return <Badge variant="success">Completed</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  }

  function getTransactionIcon(type) {
    switch (type) {
      case "delivery_payment":
        return <Package className="w-4 h-4 text-green-600" />;
      case "cashout":
        return <CreditCard className="w-4 h-4 text-blue-600" />;
      default:
        return <DollarSign className="w-4 h-4 text-gray-600" />;
    }
  }

  // Filter riders based on search and status
  const filteredRiders = riders.filter(rider => {
    const matchesSearch = !searchTerm || 
      rider.riderName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rider.riderId.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === "all" || 
      (filterStatus === "active" && rider.balance > 0) ||
      (filterStatus === "inactive" && rider.balance === 0);

    return matchesSearch && matchesStatus;
  });

  // Calculate statistics
  const totalActiveRiders = riders.filter(r => r.balance > 0).length;
  const totalWalletBalance = riders.reduce((sum, r) => sum + (r.balance || 0), 0);
  const totalEarned = riders.reduce((sum, r) => sum + (r.totalEarned || 0), 0);
  const pendingCashouts = cashoutRequests.filter(r => r.status === "pending");

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-2">Loading rider wallets...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Rider Wallets</h2>
          <p className="text-gray-600 mt-1">Manage rider earnings and cashout requests</p>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Riders</p>
              <p className="text-3xl font-bold text-gray-900">{totalActiveRiders}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Balance</p>
              <p className="text-3xl font-bold text-gray-900">₱{totalWalletBalance.toFixed(2)}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <Wallet className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Earned</p>
              <p className="text-3xl font-bold text-gray-900">₱{totalEarned.toFixed(2)}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Cashouts</p>
              <p className="text-3xl font-bold text-gray-900">{pendingCashouts.length}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-full">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search riders..."
                className="input pl-10 w-full"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              className="input"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Riders</option>
              <option value="active">Active Balance</option>
              <option value="inactive">Zero Balance</option>
            </select>
          </div>
        </div>
      </div>

      {/* Riders Table */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Rider Wallets</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rider
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current Balance
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Earned
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Withdrawn
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Activity
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRiders.map((rider) => (
                <tr key={rider.riderId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                          <UserCheck className="w-5 h-5 text-gray-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {rider.riderName || "Unknown Rider"}
                        </div>
                        <div className="text-sm text-gray-500">
                          {rider.riderEmail || rider.riderId}
                        </div>
                        {rider.riderPhone && (
                          <div className="text-xs text-gray-400">
                            📞 {rider.riderPhone}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                    ₱{Number(rider.balance || 0).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                    ₱{Number(rider.totalEarned || 0).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                    ₱{Number(rider.totalWithdrawn || 0).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {rider.lastUpdated?.toDate ? formatDT(rider.lastUpdated.toDate()) : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                    <button
                      className="text-blue-600 hover:text-blue-900"
                      onClick={() => openRiderWallet(rider)}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pending Cashout Requests */}
      {pendingCashouts.length > 0 && (
        <div className="card">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Pending Cashout Requests</h3>
            <Badge variant="warning">{pendingCashouts.length} pending</Badge>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {pendingCashouts.map((request) => (
                <div key={request.id} className="flex items-center justify-between p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-5 h-5 text-yellow-600" />
                    <div>
                      <p className="font-medium text-gray-900">
                        {request.riderName || "Unknown Rider"}
                      </p>
                      <p className="text-sm text-gray-500">
                        {request.riderEmail || request.riderId}
                      </p>
                      {request.riderPhone && (
                        <p className="text-xs text-gray-400">
                          📞 {request.riderPhone}
                        </p>
                      )}
                      <p className="text-sm text-gray-500">
                        Requested: ₱{Number(request.amount).toFixed(2)}
                      </p>
                      <p className="text-xs text-gray-400">
                        {request.createdAt?.toDate ? formatDT(request.createdAt.toDate()) : 'Unknown date'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="btn btn-sm btn-outline"
                      onClick={() => openCashoutModal(request)}
                    >
                      Review
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Rider Wallet Modal */}
      {walletModal.open && walletModal.rider && (
        <div className="fixed inset-0 z-[1100]" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]" onClick={closeWalletModal} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="card w-full max-w-4xl max-h-[90vh] overflow-hidden relative">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {walletModal.rider.riderName || "Unknown Rider"} - Wallet Details
                </h3>
                <button 
                  className="p-2 rounded hover:bg-gray-100" 
                  onClick={closeWalletModal}
                >
                  ✕
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                {/* Wallet Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-gray-600">Current Balance</p>
                    <p className="text-2xl font-bold text-green-600">
                      ₱{Number(walletModal.rider.balance || 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-gray-600">Total Earned</p>
                    <p className="text-2xl font-bold text-blue-600">
                      ₱{Number(walletModal.rider.totalEarned || 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-gray-600">Total Withdrawn</p>
                    <p className="text-2xl font-bold text-purple-600">
                      ₱{Number(walletModal.rider.totalWithdrawn || 0).toFixed(2)}
                    </p>
                  </div>
                </div>

                {/* Recent Transactions */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4">Recent Transactions</h4>
                  {riderTransactions.length === 0 ? (
                    <div className="text-center py-8">
                      <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500">No transactions found</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {riderTransactions.map((tx) => (
                        <div key={tx.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            {getTransactionIcon(tx.type)}
                            <div>
                              <p className="font-medium text-gray-900">
                                {tx.type === "delivery_payment" ? "Delivery Payment" : "Cashout"}
                              </p>
                              <p className="text-sm text-gray-500">
                                {tx.createdAt?.toDate ? formatDT(tx.createdAt.toDate()) : 'Unknown date'}
                              </p>
                              {tx.deliveryId && (
                                <p className="text-xs text-gray-400">
                                  Delivery #{tx.deliveryId.slice(0, 8)}...
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`font-semibold ${tx.type === 'delivery_payment' ? 'text-green-600' : 'text-red-600'}`}>
                              {tx.type === 'delivery_payment' ? '+' : '-'}₱{Number(tx.amount).toFixed(2)}
                            </p>
                            <Badge variant={tx.status === 'credited' ? 'success' : 'default'}>
                              {tx.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cashout Review Modal */}
      {cashoutModal.open && cashoutModal.request && (
        <div className="fixed inset-0 z-[1100]" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]" onClick={closeCashoutModal} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="card w-full max-w-md p-6 relative">
              <button 
                className="absolute right-4 top-4 p-2 rounded hover:bg-gray-100" 
                onClick={closeCashoutModal}
              >
                ✕
              </button>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Review Cashout Request</h3>
              
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-600">Rider</p>
                  <p className="text-lg font-semibold">{cashoutModal.request.riderName || "Unknown Rider"}</p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-gray-600">Requested Amount</p>
                  <p className="text-2xl font-bold text-blue-600">₱{Number(cashoutModal.request.amount).toFixed(2)}</p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-gray-600">Request Date</p>
                  <p>{cashoutModal.request.createdAt?.toDate ? formatDT(cashoutModal.request.createdAt.toDate()) : 'Unknown date'}</p>
                </div>
                
                {cashoutModal.request.notes && (
                  <div>
                    <p className="text-sm font-medium text-gray-600">Notes</p>
                    <p className="text-sm text-gray-800">{cashoutModal.request.notes}</p>
                  </div>
                )}
                
                <div className="flex gap-3 pt-4">
                  <button 
                    className="btn btn-outline flex-1"
                    onClick={() => processCashout(cashoutModal.request.id, "reject")}
                  >
                    Reject
                  </button>
                  <button 
                    className="btn btn-primary flex-1"
                    onClick={() => processCashout(cashoutModal.request.id, "approve")}
                  >
                    Approve
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
